/**
 * Voice Preferences Service
 * 
 * Manages user preferences for voice providers (STT and TTS).
 * Persists preferences to Firestore under users/{userId}/preferences/voice
 * 
 * Preferences are stored per-user and persist across sessions.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

/** @typedef {'web' | 'deepgram' | 'gemini_live'} SttProviderId */
/** @typedef {'openai' | 'deepgram' | 'gemini_flash_tts'} TtsProviderId */

/**
 * Normalize stored STT provider (migrates legacy `default` → `web`).
 * @param {string|undefined} raw
 * @returns {'web' | 'deepgram' | 'gemini_live'}
 */
export const normalizeSttProvider = (raw) => {
  if (raw === 'deepgram') return 'deepgram';
  if (raw === 'gemini_live') return 'gemini_live';
  return 'web';
};

const EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS = 3;
const EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS = 2;
const EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS = 10;
const PREFERRED_MIC_LABEL_FALLBACK_KEYWORDS = ['built-in', 'internal', 'default'];

// Default preferences
const DEFAULT_PREFERENCES = {
  // STT (single-language ChartMind path): browser Web Speech, Deepgram, or Gemini Live scribe
  sttProvider: 'web',
  /** STT when ChartMind translation mode is on (classic pipeline vs Gemini Live interpreter). */
  translationSttProvider: 'web',
  
  // TTS Provider: 'openai' | 'deepgram' (Aura) | 'gemini_flash_tts' (Gemini 3.1 Flash TTS preview)
  ttsProvider: 'openai',

  /** Native audio voice when using Gemini Live (prebuilt voice name). */
  geminiLive: {
    voice: 'Aoede',
  },
  earbudHintPauseInterval: EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS,
  preferredMicDeviceId: null,
  preferredMicDeviceLabel: null,
  
  // Deepgram-specific settings (when sttProvider is 'deepgram')
  deepgramSettings: {
    // STT model preference: 'nova3' (streaming), 'flux' (voice agent), 'whisper' (batch)
    sttModel: 'nova3',
    
    // TTS voice preference (Aura voice name)
    ttsVoice: 'asteria',
    
    // Enable code-switching for Nova-3 (en/es/fr)
    enableCodeSwitching: true,
    
    // Flux-specific settings (when using voice agent mode)
    flux: {
      eotThreshold: 0.7,       // 0.5-0.9, default 0.7
      eagerEotThreshold: 0.5,  // 0.3-0.9, must be set for EagerEndOfTurn
      eotTimeoutMs: 5000,      // 500-10000, default 5000
    },
  },
};

// Firestore path helper
const getPreferencesPath = (userId) => `users/${userId}/preferences/voice`;

/**
 * Get voice preferences for a user
 * @param {string} userId - Firebase user ID
 * @returns {Promise<Object>} User's voice preferences (merged with defaults)
 */
export const getVoicePreferences = async (userId) => {
  if (!userId) {
    console.warn('[VoicePreferences] No userId provided, returning defaults');
    return { ...DEFAULT_PREFERENCES };
  }

  try {
    const docRef = doc(db, getPreferencesPath(userId));
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      const sttProvider = normalizeSttProvider(data.sttProvider);
      const translationSttProvider = normalizeSttProvider(
        data.translationSttProvider !== undefined && data.translationSttProvider !== null
          ? data.translationSttProvider
          : sttProvider
      );

      // Merge with defaults to ensure all fields exist
      return {
        ...DEFAULT_PREFERENCES,
        ...data,
        sttProvider,
        translationSttProvider,
        geminiLive: {
          ...DEFAULT_PREFERENCES.geminiLive,
          ...data.geminiLive,
        },
        deepgramSettings: {
          ...DEFAULT_PREFERENCES.deepgramSettings,
          ...data.deepgramSettings,
          flux: {
            ...DEFAULT_PREFERENCES.deepgramSettings.flux,
            ...data.deepgramSettings?.flux,
          },
        },
      };
    }

    return { ...DEFAULT_PREFERENCES };
  } catch (error) {
    console.error('[VoicePreferences] Error loading preferences:', error);
    return { ...DEFAULT_PREFERENCES };
  }
};

/**
 * Save voice preferences for a user
 * @param {string} userId - Firebase user ID
 * @param {Object} preferences - Preferences to save (partial or full)
 * @returns {Promise<boolean>} Success status
 */
export const saveVoicePreferences = async (userId, preferences) => {
  if (!userId) {
    console.error('[VoicePreferences] Cannot save without userId');
    return false;
  }

  try {
    // Get current preferences to merge
    const current = await getVoicePreferences(userId);
    
    // Merge preferences
    const merged = {
      ...current,
      ...preferences,
      geminiLive: {
        ...current.geminiLive,
        ...preferences.geminiLive,
      },
      deepgramSettings: {
        ...current.deepgramSettings,
        ...preferences.deepgramSettings,
        flux: {
          ...current.deepgramSettings?.flux,
          ...preferences.deepgramSettings?.flux,
        },
      },
      updatedAt: serverTimestamp(),
    };

    const docRef = doc(db, getPreferencesPath(userId));
    await setDoc(docRef, merged, { merge: true });

    return true;
    
  } catch (error) {
    console.error('[VoicePreferences] Error saving preferences:', error);
    return false;
  }
};

/**
 * Set STT provider preference
 * @param {string} userId - Firebase user ID
 * @param {SttProviderId | 'default'} provider - `default` is accepted and stored as `web`
 * @returns {Promise<boolean>} Success status
 */
export const setSTTProvider = async (userId, provider) => {
  const normalized = provider === 'default' ? 'web' : normalizeSttProvider(provider);
  if (!['web', 'deepgram', 'gemini_live'].includes(normalized)) {
    console.error('[VoicePreferences] Invalid STT provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { sttProvider: normalized });
};

/**
 * Set STT provider for ChartMind translation mode (independent of {@link sttProvider}).
 */
export const setTranslationSTTProvider = async (userId, provider) => {
  const normalized = provider === 'default' ? 'web' : normalizeSttProvider(provider);
  if (!['web', 'deepgram', 'gemini_live'].includes(normalized)) {
    console.error('[VoicePreferences] Invalid translation STT provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { translationSttProvider: normalized });
};

/**
 * Set TTS provider preference
 * @param {string} userId - Firebase user ID
 * @param {TtsProviderId} provider - Provider to use (`openai` | `deepgram` | `gemini_flash_tts`)
 * @returns {Promise<boolean>} Success status
 */
export const setTTSProvider = async (userId, provider) => {
  if (!['deepgram', 'openai', 'gemini_flash_tts'].includes(provider)) {
    console.error('[VoicePreferences] Invalid TTS provider:', provider);
    return false;
  }
  return saveVoicePreferences(userId, { ttsProvider: provider });
};

/**
 * Update Deepgram-specific settings
 * @param {string} userId - Firebase user ID
 * @param {Object} settings - Deepgram settings to update
 * @returns {Promise<boolean>} Success status
 */
export const updateDeepgramSettings = async (userId, settings) => {
  return saveVoicePreferences(userId, {
    deepgramSettings: settings,
  });
};

/**
 * Get user's earbud hint pause interval (seconds)
 * @param {string} userId - Firebase user ID
 * @returns {Promise<number>} Pause interval in seconds (default 3)
 */
export const getEarbudHintPauseInterval = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  const rawValue = Number(preferences?.earbudHintPauseInterval);
  if (!Number.isFinite(rawValue)) {
    return EARBUD_HINT_PAUSE_INTERVAL_DEFAULT_SECONDS;
  }
  return Math.max(
    EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS,
    Math.min(EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS, rawValue)
  );
};

/**
 * Set user's earbud hint pause interval (seconds)
 * @param {string} userId - Firebase user ID
 * @param {number} seconds - Requested pause interval
 * @returns {Promise<boolean>} Success status
 */
export const setEarbudHintPauseInterval = async (userId, seconds) => {
  if (!Number.isFinite(seconds)) {
    console.error('[VoicePreferences] Invalid earbud hint pause interval:', seconds);
    return false;
  }

  const clampedSeconds = Math.max(
    EARBUD_HINT_PAUSE_INTERVAL_MIN_SECONDS,
    Math.min(EARBUD_HINT_PAUSE_INTERVAL_MAX_SECONDS, seconds)
  );

  return saveVoicePreferences(userId, { earbudHintPauseInterval: clampedSeconds });
};

/**
 * Get preferred microphone metadata for a user.
 * @param {string} userId - Firebase user ID
 * @returns {Promise<{deviceId: string | null, label: string | null}>}
 */
export const getPreferredMic = async (userId) => {
  const preferences = await getVoicePreferences(userId);
  const storedDeviceId = preferences?.preferredMicDeviceId;
  const storedLabel = preferences?.preferredMicDeviceLabel;

  return {
    deviceId:
      typeof storedDeviceId === 'string' && storedDeviceId.trim().length > 0
        ? storedDeviceId
        : null,
    label:
      typeof storedLabel === 'string' && storedLabel.trim().length > 0
        ? storedLabel
        : null,
  };
};

/**
 * Save preferred microphone metadata for a user.
 * @param {string} userId - Firebase user ID
 * @param {{deviceId: string, label?: string|null}} preferredMic
 * @returns {Promise<boolean>} Success status
 */
export const setPreferredMic = async (userId, preferredMic = {}) => {
  const trimmedDeviceId =
    typeof preferredMic?.deviceId === 'string' ? preferredMic.deviceId.trim() : '';
  if (!trimmedDeviceId) {
    return false;
  }

  const normalizedLabel =
    typeof preferredMic?.label === 'string' && preferredMic.label.trim().length > 0
      ? preferredMic.label
      : null;

  return saveVoicePreferences(userId, {
    preferredMicDeviceId: trimmedDeviceId,
    preferredMicDeviceLabel: normalizedLabel,
  });
};

/**
 * Resolve the best microphone device id based on stored preferences and available devices.
 * @param {MediaDeviceInfo[]} availableDevices
 * @param {string|null|undefined} preferredMicDeviceId
 * @param {string|null|undefined} preferredMicDeviceLabel
 * @returns {string|null}
 */
export const resolvePreferredMicDeviceId = (
  availableDevices,
  preferredMicDeviceId,
  preferredMicDeviceLabel
) => {
  const devices = Array.isArray(availableDevices)
    ? availableDevices.filter((device) => device && typeof device.deviceId === 'string')
    : [];
  if (!devices.length) return null;

  const trimmedPreferredId =
    typeof preferredMicDeviceId === 'string' ? preferredMicDeviceId.trim() : '';
  if (trimmedPreferredId) {
    const exactIdMatch = devices.find((device) => device.deviceId === trimmedPreferredId);
    if (exactIdMatch) return exactIdMatch.deviceId;
  }

  const normalizedPreferredLabel =
    typeof preferredMicDeviceLabel === 'string' ? preferredMicDeviceLabel.trim().toLowerCase() : '';
  if (normalizedPreferredLabel) {
    const labelMatch = devices.find((device) =>
      (device.label || '').toLowerCase().includes(normalizedPreferredLabel)
    );
    if (labelMatch?.deviceId) return labelMatch.deviceId;
  }

  const builtInMatch = devices.find((device) => {
    const normalizedLabel = (device.label || '').toLowerCase();
    return PREFERRED_MIC_LABEL_FALLBACK_KEYWORDS.some((keyword) =>
      normalizedLabel.includes(keyword)
    );
  });
  if (builtInMatch?.deviceId) return builtInMatch.deviceId;

  return null;
};

/**
 * Reset preferences to defaults
 * @param {string} userId - Firebase user ID
 * @returns {Promise<boolean>} Success status
 */
export const resetVoicePreferences = async (userId) => {
  if (!userId) {
    console.error('[VoicePreferences] Cannot reset without userId');
    return false;
  }

  try {
    const docRef = doc(db, getPreferencesPath(userId));
    await setDoc(docRef, {
      ...DEFAULT_PREFERENCES,
      updatedAt: serverTimestamp(),
    });

    return true;
    
  } catch (error) {
    console.error('[VoicePreferences] Error resetting preferences:', error);
    return false;
  }
};

/**
 * Check if Deepgram is enabled for STT
 * @param {Object} preferences - User preferences object
 * @returns {boolean}
 */
export const isDeepgramSTTEnabled = (preferences) => {
  return normalizeSttProvider(preferences?.sttProvider) === 'deepgram';
};

/** Browser Web Speech API path (+ app Whisper batch where applicable), not Deepgram or Gemini Live */
export const isWebSpeechSttProvider = (preferences) => {
  return normalizeSttProvider(preferences?.sttProvider) === 'web';
};

/** User selected Gemini Live for STT; ChartMind implements sessions (other flows fall back to web/Deepgram rules in sttFactory). */
export const isGeminiLiveSttProvider = (preferences) => {
  return preferences?.sttProvider === 'gemini_live';
};

/**
 * Check if Deepgram is enabled for TTS
 * @param {Object} preferences - User preferences object
 * @returns {boolean}
 */
export const isDeepgramTTSEnabled = (preferences) => {
  return preferences?.ttsProvider === 'deepgram';
};

/**
 * Get the appropriate Deepgram STT mode based on context
 * @param {Object} preferences - User preferences object
 * @param {Object} context - { isVoiceAgent: boolean, language: string }
 * @returns {'nova3' | 'flux' | 'whisper'}
 */
export const getDeepgramSTTMode = (preferences, context = {}) => {
  const { isVoiceAgent = false, language = 'en-US' } = context;
  
  // Voice agent mode always uses Flux
  if (isVoiceAgent) {
    return 'flux';
  }
  
  // Swahili requires Whisper Cloud (batch)
  if (language.startsWith('sw')) {
    return 'whisper';
  }
  
  // Use user's preference or default to Nova-3
  return preferences?.deepgramSettings?.sttModel || 'nova3';
};

// Export service as default object for convenience
const voicePreferencesService = {
  getVoicePreferences,
  saveVoicePreferences,
  setSTTProvider,
  setTranslationSTTProvider,
  setTTSProvider,
  updateDeepgramSettings,
  getEarbudHintPauseInterval,
  setEarbudHintPauseInterval,
  getPreferredMic,
  setPreferredMic,
  resolvePreferredMicDeviceId,
  resetVoicePreferences,
  normalizeSttProvider,
  isDeepgramSTTEnabled,
  isWebSpeechSttProvider,
  isGeminiLiveSttProvider,
  isDeepgramTTSEnabled,
  getDeepgramSTTMode,
  DEFAULT_PREFERENCES,
};

export default voicePreferencesService;
